import * as React from 'react';
import { useState } from 'react';
import { useMutation, gql } from '@apollo/client';
import {
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { Link } from 'react-router-dom';
import { FaCopy } from 'react-icons/fa';
import { AddNewStoreToInvoiceCollection } from './AddNewStoreToInvoiceCollection';
import { AddPartnerAccountToInvoiceCollection } from './AddPartnerAccountToInvoiceCollection';
import { StoreExcludeButton } from './StoreExcludeButton';
import { ManageExcludedStores } from './ManageExcludedStores';
import { PriceDifference, PriceTypes } from './PriceDifference';
import { Currency } from '@/utils/price';
import { LayoutStoreInstance } from '../../../LayoutStore';
import { InvoiceCollectionDetailFragment } from '../../../graphql/fragments/invoiceCollectionDetail.generated';
import { useRemovePartnerAccountFromInvoiceCollectionMutation } from '../../../graphql/mutations/removePartnerAccountFromInvoiceCollection.generated';
import { MerchantContainerInformationNumberOnly } from '../../../views/MerchantComponents/MerchantModel';
import { Communicator } from '../../../tools/Communication';
import { ContentSection } from '../../../components/Layout/ContentSection';
import i18n from '@/utils/i18n';

const REMOVE_STORE_FROM_INVOICE_COLLECTION = gql`
  mutation RemoveStoreFromInvoiceCollection(
    $invoiceCollectionId: ID!
    $storeId: ID!
  ) {
    removeStoreFromInvoiceCollection(
      invoice_collection_id: $invoiceCollectionId
      store_id: $storeId
    ) {
      id
    }
  }
`;

type SelectedStoreType = {
  id: string;
  name: string;
};

type InvoiceCollectionStoresAccountsTabProps = {
  invoiceCollectionId: string;
  invoiceCollection: InvoiceCollectionDetailFragment;
  refetchInvoice: () => void;
  containerPrices: MerchantContainerInformationNumberOnly[];
  setContainerPrices: (
    prices: MerchantContainerInformationNumberOnly[],
  ) => void;
};

export const InvoiceCollectionStoresAccountsTab: React.FC<
  InvoiceCollectionStoresAccountsTabProps
> = ({
  invoiceCollectionId,
  invoiceCollection,
  refetchInvoice,
  containerPrices,
  setContainerPrices,
}) => {
  const [isRemoveStoreModalOpen, setIsRemoveStoreModalOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState<SelectedStoreType>();

  const [removeStore] = useMutation(REMOVE_STORE_FROM_INVOICE_COLLECTION, {
    onCompleted: () => refetchInvoice(),
  });
  const [removePartnerAccount] =
    useRemovePartnerAccountFromInvoiceCollectionMutation({
      onCompleted: () => refetchInvoice(),
    });

  const resolvePriceConflict = async (
    type: PriceTypes,
    value: number,
    containerTypeId: number,
  ) => {
    const newPrices = [...containerPrices];
    newPrices.forEach((price) => {
      if (price.containerTypeId === containerTypeId) {
        if (type === PriceTypes.TRANSACTION) {
          price.transactionPrice = value;
        }
      }
    });
    setContainerPrices(newPrices);
    const update: any = {
      containerTypeId,
      storeIds: invoiceCollection.stores
        .map((store) => store?.external_vytal_id)
        .filter((id) => id != null),
    };
    if (type === PriceTypes.TRANSACTION) {
      update.transactionPrice = value;
    }
    const res = await Communicator.PostData(
      'container/UpdateContainerPriceForStore',
      update,
    );
    const data = await res.json();
    setContainerPrices(data);
  };

  const storeIdsRaw = ((
    invoiceCollection as unknown as {
      store_ids?: (string | null | undefined)[];
    }
  )?.store_ids ?? []) as (string | null | undefined)[];
  const includedStoreIds = new Set(storeIdsRaw.filter((x): x is string => !!x));

  return (
    <>
      <ContentSection
        title={i18n.t('InvoiceCollection:titleLinkedStores')}
        collapsable={false}
      >
        <Modal
          isOpen={isRemoveStoreModalOpen}
          toggle={() => {
            setIsRemoveStoreModalOpen(false);
          }}
        >
          <ModalHeader
            toggle={() => {
              setIsRemoveStoreModalOpen(false);
            }}
          >
            {i18n.t('InvoiceCollection:modalTitle')}
          </ModalHeader>
          <ModalBody>
            {i18n.t('InvoiceCollection:modalBody')}: <p></p>
            {i18n.t('Common:id')}: <p>{selectedStore?.id}</p>
            {i18n.t('Common:name')}: <p>{selectedStore?.name}</p>
          </ModalBody>
          <ModalFooter>
            <Button
              color="danger"
              onClick={async () => {
                try {
                  await removeStore({
                    variables: {
                      invoiceCollectionId: invoiceCollectionId,
                      storeId: selectedStore?.id,
                    },
                  });
                } catch (err: any) {
                  LayoutStoreInstance.setError(
                    'Error removing store from invoice collection: ' +
                      err?.message,
                  );
                }

                setIsRemoveStoreModalOpen(false);
              }}
            >
              {i18n.t('Common:yes')}
            </Button>{' '}
            <Button
              color="secondary"
              onClick={() => {
                setIsRemoveStoreModalOpen(false);
              }}
            >
              {i18n.t('Common:no')}
            </Button>
          </ModalFooter>
        </Modal>

        <Table striped bordered>
          <thead>
            <tr>
              <th>{i18n.t('Common:store')}</th>
              <th>{i18n.t('Common:id')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {invoiceCollection.stores.map((store: any) => (
              <tr key={store.id}>
                <td>
                  <Link to={`/pickpack/stores/${store.id}`}>{store.name}</Link>
                </td>
                <td
                  onClick={() => {
                    navigator.clipboard.writeText(store.id);
                    LayoutStoreInstance.setSuccess(i18n.t('Common:copied'));
                  }}
                >
                  {store.id}
                </td>
                <td>
                  {(() => {
                    const storeIdsRaw = ((
                      invoiceCollection as unknown as {
                        store_ids?: (string | null | undefined)[];
                      }
                    )?.store_ids ?? []) as (string | null | undefined)[];
                    const included = new Set(
                      storeIdsRaw.filter((x): x is string => !!x),
                    );
                    return included.has(store.id) ? (
                      <button
                        style={{ marginLeft: '10px' }}
                        className="btn btn-sm btn-danger"
                        onClick={() => {
                          setSelectedStore(store);
                          setIsRemoveStoreModalOpen(true);
                        }}
                      >
                        {i18n.t('Common:remove')}
                      </button>
                    ) : (
                      <StoreExcludeButton
                        invoiceCollectionId={invoiceCollectionId}
                        storeId={store.id}
                        existingExcludedIds={(
                          ((
                            invoiceCollection as unknown as {
                              excluded_store_ids?: (
                                | string
                                | null
                                | undefined
                              )[];
                            }
                          )?.excluded_store_ids ?? []) as (
                            | string
                            | null
                            | undefined
                          )[]
                        ).filter((x): x is string => !!x)}
                        onDone={() => refetchInvoice()}
                      />
                    );
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {invoiceCollection.stores.length === 0 && (
          <p>{i18n.t('InvoiceCollection:invoiceNotUsedInStores')}</p>
        )}
        <AddNewStoreToInvoiceCollection
          invoiceCollectionId={invoiceCollectionId}
        />
      </ContentSection>

      <br />

      <ContentSection
        title={i18n.t('InvoiceCollection:titleLinkedPartnerAccounts')}
        collapsable={false}
      >
        {invoiceCollection.partner_accounts.length > 0 ? (
          <Table striped bordered>
            <thead>
              <tr>
                <th>{i18n.t('Common:partnerAccount')}</th>
                <th>{i18n.t('Common:id')}</th>
                <th>{i18n.t('InvoiceCollection:accountHierarchy')}</th>
                <th>{i18n.t('InvoiceCollection:accountStoreCount')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {invoiceCollection.partner_accounts.map((account) => (
                <tr key={account.id}>
                  <td>{account.name || account.company_name || account.id}</td>
                  <td
                    onClick={() => {
                      navigator.clipboard.writeText(account.id);
                      LayoutStoreInstance.setSuccess(i18n.t('Common:copied'));
                    }}
                  >
                    {account.id}
                  </td>
                  <td>{account.partner_account_path}</td>
                  <td>{account.store_count}</td>
                  <td>
                    <button
                      style={{ marginLeft: '10px' }}
                      className="btn btn-sm btn-danger"
                      onClick={async () => {
                        try {
                          await removePartnerAccount({
                            variables: {
                              invoiceCollectionId: invoiceCollectionId,
                              partnerAccountId: account.id,
                            },
                          });
                        } catch (err: unknown) {
                          LayoutStoreInstance.setError(
                            'Error removing partner account from invoice collection: ' +
                              (err as Error)?.message,
                          );
                        }
                      }}
                    >
                      {i18n.t('Common:remove')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <p>{i18n.t('InvoiceCollection:invoiceNotUsedInPartnerAccounts')}</p>
        )}
        <AddPartnerAccountToInvoiceCollection
          invoiceCollectionId={invoiceCollectionId}
          onAdded={() => refetchInvoice()}
        />
      </ContentSection>

      <br />

      <ContentSection
        title={i18n.t('InvoiceCollection:excludedStores')}
        collapsable={false}
      >
        <ManageExcludedStores
          invoiceCollectionId={invoiceCollectionId}
          excludedStores={invoiceCollection.excluded_stores
            .filter((s) => s != null)
            .map((s) => ({ id: s.id, name: s.name || '' }))}
          linkedStores={invoiceCollection.stores
            .filter((s) => s != null)
            .map((s) => ({ id: s.id, name: s.name || '' }))}
          refetch={async () => {
            await refetchInvoice();
          }}
        />
      </ContentSection>

      <br />

      <ContentSection
        title={i18n.t('InvoiceCollection:priceDifference')}
        collapsable={false}
      >
        <PriceDifference
          containerPrices={containerPrices}
          onResolve={resolvePriceConflict}
          currency={
            invoiceCollection.stores.find(() => true)?.currency || Currency.EUR
          }
        />
      </ContentSection>
    </>
  );
};
